/**
 * Bug Report Modal
 *
 * Modal for users to submit bug reports with system diagnostics.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  X,
  Bug,
  Upload,
  Image,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { api, type BugReportCategory } from '../../services/api';
import { collectSystemInfo, getSystemInfoSummary, type SystemInfo } from '../../services/system-info';
import { useLanguage } from '../../contexts/LanguageContext';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export function BugReportModal({ isOpen, onClose, onSuccess }: BugReportModalProps) {
  const { t } = useLanguage();

  const CATEGORIES: { value: BugReportCategory; label: string; description: string }[] = [
    { value: 'display_issue', label: t('bugReport.categories.displayIssue.label', 'Display Issue'), description: t('bugReport.categories.displayIssue.description', 'Something looks wrong or broken visually') },
    { value: 'crash_freeze', label: t('bugReport.categories.crashFreeze.label', 'Crash / Freeze'), description: t('bugReport.categories.crashFreeze.description', 'App crashed, froze, or became unresponsive') },
    { value: 'data_content_error', label: t('bugReport.categories.dataContentError.label', 'Data / Content Error'), description: t('bugReport.categories.dataContentError.description', 'Incorrect prices, missing products, wrong info') },
    { value: 'performance_problem', label: t('bugReport.categories.performanceProblem.label', 'Performance Problem'), description: t('bugReport.categories.performanceProblem.description', 'Slow loading, lag, or high resource usage') },
    { value: 'feature_not_working', label: t('bugReport.categories.featureNotWorking.label', 'Feature Not Working'), description: t('bugReport.categories.featureNotWorking.description', 'A button or feature does not work as expected') },
    { value: 'login_account_issue', label: t('bugReport.categories.loginAccountIssue.label', 'Login / Account Issue'), description: t('bugReport.categories.loginAccountIssue.description', 'Problems signing in or with account settings') },
    { value: 'label_scan_issue', label: t('bugReport.categories.labelScanIssue.label', 'Label Scan Issue'), description: t('bugReport.categories.labelScanIssue.description', 'As-Is label would not scan correctly') },
    { value: 'other', label: t('bugReport.categories.other.label', 'Other'), description: t('bugReport.categories.other.description', 'Something else not listed above') },
  ];
  const [category, setCategory] = useState<BugReportCategory | ''>('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotMimeType, setScreenshotMimeType] = useState<string>('');
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [showSystemInfo, setShowSystemInfo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);


  // Collect system info when modal opens
  const collectInfo = useCallback(async () => {
    setIsLoading(true);
    try {
      const info = await collectSystemInfo();
      setSystemInfo(info);
    } catch (err) {
      console.error('Failed to collect system info:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle modal open/close
  if (isOpen && !systemInfo && !isLoading) {
    collectInfo();
  }

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('bugReport.errors.invalidFileType', 'Please select an image file (PNG or JPG)'));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(t('bugReport.errors.fileTooLarge', 'Image must be smaller than 5MB'));
      return;
    }

    setError(null);
    setScreenshotMimeType(file.type);

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setScreenshotPreview(result);
      // Extract base64 data (remove data:image/...;base64, prefix)
      const base64 = result.split(',')[1];
      setScreenshot(base64);
    };
    reader.readAsDataURL(file);
  }, [t]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    // Create a synthetic event for the file handler
    const input = fileInputRef.current;
    if (input) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      handleFileSelect({ target: input } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeScreenshot = useCallback(() => {
    setScreenshot(null);
    setScreenshotPreview(null);
    setScreenshotMimeType('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!category) {
      setError(t('bugReport.errors.selectCategory', 'Please select a category'));
      return;
    }

    if (!description.trim()) {
      setError(t('bugReport.errors.describeIssue', 'Please describe the issue'));
      return;
    }

    if (description.trim().length < 10) {
      setError(t('bugReport.errors.descriptionTooShort', 'Please provide a more detailed description (at least 10 characters)'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await api.submitBugReport({
        category,
        description: description.trim(),
        systemInfo: systemInfo ? {
          browser: systemInfo.browser,
          browserVersion: systemInfo.browserVersion,
          os: systemInfo.os,
          osVersion: systemInfo.osVersion,
          platform: systemInfo.platform,
          isMobile: systemInfo.isMobile,
          viewportWidth: systemInfo.viewportWidth,
          viewportHeight: systemInfo.viewportHeight,
          screenWidth: systemInfo.screenWidth,
          screenHeight: systemInfo.screenHeight,
          currentUrl: systemInfo.currentUrl,
          appVersion: systemInfo.appVersion,
          online: systemInfo.online,
          language: systemInfo.language,
          timezone: systemInfo.timezone,
          recentErrors: systemInfo.recentErrors,
        } : {},
        screenshot: screenshot || undefined,
        screenshotMimeType: screenshotMimeType || undefined,
        platform: 'web',
      });

      setSuccess(true);
      onSuccess?.();

      // Close after showing success message
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      console.error('Failed to submit bug report:', err);
      setError(err instanceof Error ? err.message : t('bugReport.errors.submitFailed', 'Failed to submit bug report. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setCategory('');
    setDescription('');
    setScreenshot(null);
    setScreenshotPreview(null);
    setScreenshotMimeType('');
    setSystemInfo(null);
    setShowSystemInfo(false);
    setError(null);
    setSuccess(false);
    onClose();
  };

  // Handle Escape key to close modal (with stopPropagation to prevent ProfileMenu from closing)
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleClose is stable and including it would cause unnecessary re-subscriptions
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-[60] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label={t('bugReport.title', 'Report a Bug')}
      data-bug-report-modal
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                <Bug className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('bugReport.title', 'Report a Bug')}</h2>
                <p className="text-sm text-gray-500">{t('bugReport.subtitle', 'Help us improve by reporting issues')}</p>
              </div>
            </div>
          </div>

          {/* Success State */}
          {success && (
            <div className="p-6">
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('bugReport.success.title', 'Thank You!')}</h3>
                <p className="text-gray-600 text-center">
                  {t('bugReport.success.message', 'Your bug report has been submitted successfully. We appreciate your feedback!')}
                </p>
              </div>
            </div>
          )}

          {/* Form */}
          {!success && (
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Error message */}
              {error && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Category */}
              <div>
                <label htmlFor="bug-category" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bugReport.form.categoryLabel', 'What kind of issue is this?')} *
                </label>
                <select
                  id="bug-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as BugReportCategory)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isSubmitting}
                >
                  <option value="">{t('bugReport.form.selectCategory', 'Select a category...')}</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                {category && (
                  <p className="mt-1 text-xs text-gray-500">
                    {CATEGORIES.find((c) => c.value === category)?.description}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="bug-description" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bugReport.form.descriptionLabel', 'Describe the issue')} *
                </label>
                <textarea
                  id="bug-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('bugReport.form.descriptionPlaceholder', 'What happened? What were you trying to do? Any error messages?')}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={isSubmitting}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {description.length} {t('bugReport.form.characters', 'characters')}
                </p>
              </div>

              {/* Screenshot Upload */}
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  {t('bugReport.form.screenshotLabel', 'Screenshot (optional)')}
                </span>

                {screenshotPreview ? (
                  <div className="relative">
                    <img
                      src={screenshotPreview}
                      alt={t('bugReportModal.screenshotPreviewAlt', 'Screenshot preview')}
                      className="w-full h-40 object-contain bg-gray-100 rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 cursor-pointer transition-colors"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        <Upload className="w-5 h-5 text-gray-400" />
                        <Image className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600">
                        {t('bugReport.form.dragDrop', 'Drag & drop or click to upload')}
                      </p>
                      <p className="text-xs text-gray-400">
                        {t('bugReport.form.fileHint', 'PNG or JPG, max 5MB')}
                      </p>
                    </div>
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* System Info */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowSystemInfo(!showSystemInfo)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  {showSystemInfo ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <span>{t('bugReport.form.diagnosticInfo', 'Diagnostic Information')}</span>
                  {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                </button>

                {showSystemInfo && systemInfo && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 font-mono">
                    <p className="mb-1">
                      <span className="text-gray-400">{t('bugReport.systemInfo.browser', 'Browser')}:</span> {systemInfo.browser} {systemInfo.browserVersion}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-400">{t('bugReport.systemInfo.os', 'OS')}:</span> {systemInfo.os} {systemInfo.osVersion}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-400">{t('bugReport.systemInfo.viewport', 'Viewport')}:</span> {systemInfo.viewportWidth}x{systemInfo.viewportHeight}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-400">{t('bugReport.systemInfo.page', 'Page')}:</span> {systemInfo.currentUrl}
                    </p>
                    <p className="mb-1">
                      <span className="text-gray-400">{t('bugReport.systemInfo.appVersion', 'App Version')}:</span> {systemInfo.appVersion}
                    </p>
                    {systemInfo.recentErrors.length > 0 && (
                      <p className="text-amber-600">
                        {systemInfo.recentErrors.length} {t('bugReport.systemInfo.recentErrors', 'recent error(s) captured')}
                      </p>
                    )}
                  </div>
                )}

                {!showSystemInfo && systemInfo && (
                  <p className="mt-1 text-xs text-gray-400">
                    {getSystemInfoSummary(systemInfo)}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('bugReport.form.submitting', 'Submitting...')}
                    </>
                  ) : (
                    <>
                      <Bug className="w-5 h-5" />
                      {t('bugReport.form.submit', 'Submit Bug Report')}
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                {t('bugReport.form.disclaimer', 'By submitting, you agree to share diagnostic information to help us investigate.')}
              </p>
            </form>
          )}
    </div>
  );
}
